/**
 * Small shared helpers that decide whether an exercise entry has a real
 * video (Cloudinary or otherwise) or just an image, and render either a
 * `<video>` with poster + `preload="none"` (never autoplay) or a plain
 * `<img>` fallback.
 *
 * The Cloudinary optimisation transform (`f_auto,q_auto`) is only injected
 * when the URL matches Cloudinary's CDN; other URLs pass through so we
 * don't break older seed data that points at Unsplash images.
 */

const isVideoUrl = (u) =>
  !!u && /\.(mp4|webm|mov|m4v)(\?|$)/i.test(u);

const isCloudinary = (u) => !!u && /res\.cloudinary\.com\//.test(u);

/**
 * Return a mobile-optimized delivery URL for a Cloudinary video/image.
 * Falls back untouched for non-Cloudinary URLs so anything already in
 * the database keeps working.
 */
export function optimizedUrl(url, kind = "video") {
  if (!url || !isCloudinary(url)) return url;
  const marker = kind === "image" ? "/image/upload/" : "/video/upload/";
  if (!url.includes(marker)) return url;
  // Skip if the URL was already built with any transformation.
  const [prefix, rest] = url.split(marker);
  if (/^[a-z_]{2,}/.test(rest.split("/")[0])) return url;
  return `${prefix}${marker}f_auto,q_auto${kind === "image" ? ",w_400" : ""}/${rest}`;
}

/** Build a jpg poster URL from a Cloudinary video URL. */
export function posterFromVideo(url) {
  if (!url || !isCloudinary(url)) return undefined;
  return url
    .replace("/video/upload/", "/video/upload/so_1,w_640,q_auto,f_jpg/")
    .replace(/\.(mp4|webm|mov|m4v)(\?|$)/i, ".jpg$2");
}

/** Small thumbnail used in lists / builder rows / patient plan card. */
export function ExerciseThumb({ ex, className = "" }) {
  const thumb = ex?.thumbnail_url
    || (ex?.video_url && posterFromVideo(ex.video_url))
    || ex?.video_url;
  if (!thumb) return <div className={`bg-white/5 ${className}`} />;
  return (
    <img
      src={thumb}
      alt=""
      loading="lazy"
      className={`object-cover ${className}`}
      data-testid="ex-thumb"
    />
  );
}

/**
 * Prominent player used on the patient plan + session runner.
 * `preload="none"` + poster keeps mobile data usage minimal until tap.
 */
export function ExerciseVideo({ ex, className = "" }) {
  if (!ex) return null;
  const url = ex.video_url;
  if (!url) return null;
  if (!isVideoUrl(url)) {
    // Old seed rows (Unsplash images) — render as image so patient-facing
    // pages don't blow up. Programme Builder still keeps them usable.
    return (
      <img
        src={url}
        alt={ex.name || ""}
        className={`w-full object-cover ${className}`}
        data-testid="ex-video-fallback-img"
      />
    );
  }
  const src = optimizedUrl(url, "video");
  const poster = ex.thumbnail_url || posterFromVideo(url);
  return (
    <video
      src={src}
      poster={poster}
      controls
      playsInline
      preload="none"
      className={`w-full bg-black ${className}`}
      data-testid="ex-video"
    >
      Your browser cannot play this video.
    </video>
  );
}

/**
 * Upload a video variant against a specific approved-library exercise
 * using the *server-controlled* Cloudinary sign/confirm flow (SEC audit
 * iter104):
 *
 *   1. `POST /api/exercises/{id}/videos/sign?variant=…`  ← server picks
 *      the full `public_id` (`functional-lab/exercises/{library_key}/{variant}`)
 *   2. Direct browser upload to Cloudinary with that signed payload
 *   3. `POST /api/exercises/{id}/videos/confirm`         ← server verifies
 *      the returned `public_id` matches the exact prefix it just signed
 *      before touching the DB
 *
 * Progress is reported via the optional `onProgress(pct)` callback so the
 * caller can render a progress bar. Returns the refreshed exercise doc.
 */
export async function uploadExerciseVideo({ file, api, exerciseId, variant, onProgress }) {
  const { data: sig } = await api.post(
    `/exercises/${exerciseId}/videos/sign`,
    null,
    { params: { variant } },
  );
  const form = new FormData();
  form.append("file", file);
  form.append("api_key", sig.api_key);
  form.append("timestamp", sig.timestamp);
  form.append("signature", sig.signature);
  form.append("public_id", sig.public_id);
  form.append("overwrite", "true");
  form.append("invalidate", "true");

  // Use XHR so we get real byte-level progress that <fetch> can't provide.
  const uploaded = await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `https://api.cloudinary.com/v1_1/${sig.cloud_name}/video/upload`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch (err) { reject(new Error("Cloudinary returned non-JSON")); }
      } else {
        reject(new Error(`Cloudinary upload failed: ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error("Network error uploading to Cloudinary"));
    xhr.send(form);
  });

  const thumbnail_url = uploaded.secure_url
    ? uploaded.secure_url
        .replace("/video/upload/", "/video/upload/so_1,w_640,q_auto,f_jpg/")
        .replace(/\.(mp4|webm|mov|m4v)(\?|$)/i, ".jpg$2")
    : undefined;

  const { data: confirmed } = await api.post(
    `/exercises/${exerciseId}/videos/confirm`,
    {
      variant,
      public_id: uploaded.public_id,
      secure_url: uploaded.secure_url,
      duration: uploaded.duration,
      thumbnail_url,
      width: uploaded.width,
      height: uploaded.height,
    },
  );
  return confirmed;
}

// SEC-iter105 · The legacy `uploadToCloudinary` (generic `folder`-based
// signature) was removed. All exercise-video uploads now go through the
// per-exercise `uploadExerciseVideo` above, which uses a server-generated
// `public_id` (`functional-lab/exercises/{library_key}/{variant}`) and
// re-verifies the returned asset in a `confirm` step. That's the only
// pathway that touches the `exercises.videos[]` array — no other caller
// should reintroduce a folder-scoped signature.
